import {LitElement, html, nothing} from 'lit';
import {BopisController} from 'custom-extensions/bopis';
import LoadingUI from 'extensions/loading-ui';
import styles from './BopisDrawer.component.scss?as=stylesheet';
import EventBus from '@mvps-genesis/event-bus';
import {classMap} from 'lit/directives/class-map.js';

export default class BopisDrawer extends LitElement {

	/**
	 * Define stylesheets
	 * @see https://lit.dev/docs/components/styles/
	 * @static
	 */
	static get styles () {
		return [styles];
	}

	/**
	 * Define reflector properties
	 * @see https://lit.dev/docs/components/properties/
	 * @static
	 */
	static get properties () {
		return {
			basketBopisStore: {
				type: Object
			},
			basketfields: {
				type: Object
			},
			basketgroup: {
				type: String
			},
			formSubmitted: {
				type: Boolean
			},
			isLoading: {
				type: Boolean
			},
			productfields: {
				type: Object
			},
			resultMessage: {
				type: String
			},
			results: {
				type: Array
			},
			showRadius: {
				type: Boolean
			},
			term: {
				type: String
			},
			type: {
				type: String
			}
		};
	};

	/**
	 * @constructor
	 */
	constructor () {
		super();

		this.basketBopisStore = {};
		this.basketfields = {};
		this.basketgroup = '';
		this.controller = BopisController;
		this.footer = null;
		this.formSubmitted = false;
		this.isLoading = false;
		this.loadingUI = false;
		this.opened = false;
		this.productfields = {};
		this.resultMessage = '';
		this.results = [];
		this.searchElem = null;
		this.showRadius = false;
		this.type = 'mystore';
		this.modal = this.closest('g-modal');
	}

	/**
	 * Called after the element's DOM has been updated the first time, immediately before updated is called.
	 * @see https://lit.dev/docs/v1/components/lifecycle/#firstupdated
	 * @static
	 */
	firstUpdated () {
		this.setLoadingUI();
		this.setListeners();
		this.searchElem = this.renderRoot.querySelector('.g-bopis-drawer-form [name="Search"]');
		this.footer = this.renderRoot.querySelector('.g-bopis-drawer-footer');

		if (this.type === 'product') {
			this.productfields = window?.mivaJS?.BopisProductFields || {};
		}

		if (this.type === 'basket' && this.basketBopisStore) {
			this.getBasketStoreInformation();
		}
	}

	/**
	 * Invoked when a component is added to the document's DOM.
	 * @see https://lit.dev/docs/components/lifecycle/#connectedcallback
	 * @static
	 */
	connectedCallback () {
		super.connectedCallback();
		this.bopisStoreUnsubscribe = this.controller.store.subscribe(() => this.requestUpdate());

		if (this.type === 'basket') {
			EventBus.addEventListener('MVPS:shopStoreBasketChanged', this.shopStoreBasketChanged.bind(this), false);
		}
	}

	/**
	 * Invoked when a component is removed from the document's DOM.
	 * @see https://lit.dev/docs/components/lifecycle/#disconnectedcallback
	 * @static
	 */
	disconnectedCallback () {
		this.bopisStoreUnsubscribe();
		if (this.type === 'basket') {
			EventBus.removeEventListener('MVPS:shopStoreBasketChanged', this.shopStoreBasketChanged.bind(this), false);
		}

		super.disconnectedCallback();
	}

	/**
	 * @see https://lit.dev/docs/components/rendering/
	 */
	render () {
		const classes = {
			'g-bopis-drawer': true,
			'has-current-store': this.hasMyStore
		};

		return html`
			<div class="${classMap(classes)}">
				<div class="js-bopis-drawer-loading-container" aria-hidden="true"></div>
				<button type="button" @click="${this.modal?.close}" class="g-bopis-drawer__close" aria-label="Close Modal"></button>
				${this.drawMyStore()}
				<div class="g-bopis-drawer-header">
					${this.drawLocationTitle(this.hasMyStore ? 'Find Another Store' : 'Select Your Store')}
					<div class="g-bopis-drawer-use-location">
						<button class="g-bopis-drawer-use-location__button g-button--no-styling" @click="${this.useCurrentLocation}">
							<span class="g-bopis-drawer-use-location__text">Use my current location</span>
						</button>
						<a class="g-bopis-drawer-view-all__link" href="${window.mivaJS?.locationURLBase}">View All Store Locations</a>
					</div>
					<form class="g-bopis-drawer-form" @submit="${this.handleFormSubmission}">
						<div class="g-bopis-drawer-input-wrapper g-control-group u-flex">
							<input class="g-bopis-drawer-form__input g-form-input g-control-group__field u-bg-transparent u-border-none" type="text" name="Search" .value="${this.controller?.store?.state?.lastSearch?.term || ''}" placeholder="Search By Zip or City.." required autocomplete="false" />
							<button type="submit" class="g-bopis-drawer-form__button g-button g-control-group__button u-bg-transparent u-icon-search u-border-none">
								<span class="u-hide-visually">Search</span>
							</button>
						</div>
					</form>
					${this.drawFormattedTerm()}
					${this.drawMessages()}
				</div>
				<div class="g-bopis-drawer-footer">
					${this.drawResults()}
					${this.drawDefaultMessage()}
				</div>
			</div>
		`;
	}

	/**
	 * Requests the user's location to populate the search term.
	 */
	async useCurrentLocation () {
		this.setLoading(true);
		const getCurrentLocation = await this.controller.useCurrentLocation();
		this.setLoading(false);

		if (getCurrentLocation) {
			this.handleFormSubmission(null);
		}
	}

	/**
	 * Handles the Form Submission.
	 * @param Event	e
	 */
	handleFormSubmission (e) {
		if (e) {
			e.preventDefault();
		}

		this.search();
	}

	search () {
		if (this.type === 'product') {
			this.productSearch();
		} else if (this.type === 'basket') {
			this.basketSearch();
		} else {
			this.storeSearch();
		}
	}

	/**
	 * Performs a Location Search (Not used for product or basket)
	 */
	async storeSearch () {
		this.setLoading(true);
		this.formSubmitted = true;

		const results = await this.controller.storeSearch(this.searchElem?.value || '');
		this.updateFooter(results);

		this.setLoading(false);
	}

	/**
	 * Performs a Location Search for a Specific Product
	 * @param	{Object}	productfields	Object of all product fields (custom fields)
	 */
	async productSearch () {
		this.setLoading(true);
		this.formSubmitted = true;

		const results = await this.controller.productStoreSearch(this.searchElem?.value || '', this.productfields.bopis_inventory, 1);

		this.updateFooter(results);

		this.setLoading(false);
	}

	/**
	 * Performs a Location Search for the Basket Page
	 */
	async basketSearch () {
		this.setLoading(true);
		this.formSubmitted = true;

		const results = await this.controller.productStoreSearch(this.searchElem?.value || '', this.basketfields?.bopis_inventory, this.basketfields?.basketQuantity);
		this.updateFooter(results);

		this.setLoading(false);
	}

	/**
	 * Clear out the results & message.
	 */
	clearResults () {
		this.results = [];
		this.resultMessage = '';
		this.showRadius = false;
	}

	/**
	 * Resets the variables managed by the Drawer
	 */
	reset () {
		this.formSubmitted = false;
		this.clearResults();
	}

	get hasMyStore () {
		return this.controller?.store?.state?.myStore?.identifier && this.controller?.store?.state?.myStore?.name;
	}

	/**
	 * Draws a LocationCard element "My Store"
	 * @return {html}
	 */
	drawMyStore () {
		if (!this.controller?.store?.state?.myStore?.identifier || !this.controller?.store?.state?.myStore?.name) {
			return nothing;
		}

		let elements = [];
		elements.push(this.drawLocationHeading('Your Store'));

		const location = {...this.controller.store.state.myStore};
		if (this.type === 'product') {
			this.controller.api.productInventoryStore(this.productfields.bopis_inventory, location, 1);
		} else if (this.type === 'basket') {
			this.controller.api.productInventoryStore(this.basketfields?.bopis_inventory, location, this.basketfields?.basketQuantity);
		}

		elements.push(this.drawLocationCard(location, true));

		return html`<div class="g-bopis-drawer-top">${elements}</div>`;
	}

	/**
	 * Draws the Results if there are any
	 * @return {html}
	 */
	drawResults () {
		const formattedResults = this.formattedResults.filter((store) => !this.isMyStore(store));
		let elements = [];

		if (formattedResults?.length === 0) {
			return nothing;
		}

		for (const store of formattedResults) {
			if (!store.identifier) {
				continue;
			}

			elements.push(this.drawLocationCard(store, false));
		}

		if (elements.length) {
			if (this.type === 'product' || this.type === 'basket') {
				elements.unshift(this.drawLocationTitle(this.showRadius ? 'Pick Up Stores Near You' : 'Store Pick Up Locations'));
			} else {
				elements.unshift(this.drawLocationTitle(this.showRadius ? 'Stores Near You' : 'Store Locations'));
			}
		}

		return html`${elements}`;
	}

	/**
	 * Draws a single Location card
	 * @param	{Object}	location	Location Element
	 * @param	{Boolean}	titleCard	If it's a titlecard or not.
	 * @return	{html}
	 */
	drawLocationCard (store = {}, titleCard = false) {
		let fields = this.productfields;
		if (this.type === 'basket') {
			fields = this.basketfields;
		}

		return html`
			<x-location-card
				.store="${store}"
				.type="${this.type}"
				.titleCard="${titleCard}"
				.inStoreOnly="${fields?.inStoreOnly}"
				.excludeBopis="${fields?.exclude_store_pickup}"
				.basketBopisStore="${this.basketBopisStore}"
				.basketgroup="${this.basketgroup}"
				?show-radius="${this.showRadius}"
			>
			</x-location-card>
		`;
	}

	/**
	 * Draws a Title
	 * @param	{String} text
	 * @return	{html}
	 */
	drawLocationTitle (text) {
		return html`<div class="g-bopis-drawer-locations-title">${text}</div>`;
	}

	/**
	 * Draws a Heading
	 * @param	{String} text
	 * @return	{html}
	 */
	drawLocationHeading (text) {
		return html`<div class="g-bopis-drawer-locations-heading">${text}</div>`;
	}

	/**
	 * Draws out elements for state.messages and result messages
	 * @return {html}
	 */
	drawMessages () {
		const output = [];

		if (this.controller?.state?.messages) {
			output.push(html`
				<div class="g-bopis-drawer-messages">${this.controller.state.messages}</div>
			`);
		}

		if (this.resultMessage) {
			output.push(html`
				<div class="g-bopis-drawer-message">
					<div class="g-bopis-drawer-message__text">
						${this.resultMessage}
					</div>
				</div>
			`);
		}

		if (output.length === 0) {
			return nothing;
		}

		return html`${output}`;
	}

	/**
	 * Renders the formatted term from the geolocation search
	 * @return {html}
	 */
	drawFormattedTerm () {
		if (!this.controller?.store?.state?.lastSearch?.formattedTerm) {
			return nothing;
		}

		if (this.controller?.store?.state?.lastSearch?.formattedTerm === this.controller?.store?.state?.lastSearch?.term) {
			return nothing;
		}

		return html`
			<div class="g-bopis-drawer-formatted-term">Searching Stores Near "${this.controller.store.state.lastSearch.formattedTerm}"</div>
		`;
	}

	/**
	 * Draws out elements for state.messages and result messages
	 * @return {html}
	 */
	drawDefaultMessage () {
		if (this.formSubmitted === true) {
			return nothing;
		}

		return html`
			<div class="g-bopis-drawer-message g-bopis-drawer-default-message">
				<div class="g-bopis-drawer-message__text">
					Search By Zip Code, City Or
				</div>
				<a class="g-bopis-drawer-message__link" href="${window.mivaJS?.locationURLBase}">Use Map View</a>
			</div>
		`;
	}

	/**
	 * Sets the `results` and `resultMessage`, as well as scrolling the `footer` back to 0.
	 * @param	{Object}	results
	 * @prop	{Array}		results.locations	Array of Locations
	 * @prop	{String}	results.message		Message to display (if applicable)
	 */
	updateFooter (results) {
		this.results = results.locations;
		this.resultMessage = results.message;
		this.showRadius = results.showRadius;

		if (this.footer) {
			this.footer.scrollTop = 0;
		}
	}

	/**
	 * Returns the formatted results by sorting the results in the following format:
	 * currently selected (if prod/bask), myStore (regardless of inventory), in stock locations, out of stock locations, normal sort from uberall (distance)
	 */
	get formattedResults () {
		const locations = [];
		const myStore = [];
		const currentlySelected = [];
		const inStockLocations = [];
		const outOfStockLocations = [];

		for (const result of this.results) {
			if (this.isMyStore(result)) {
				myStore.push(result);
				continue;
			}

			if (this.type !== 'product' && this.type !== 'basket') {
				locations.push(result);
				continue;
			}

			if (this.basketBopisStore?.identifier?.toString() === result?.identifier?.toString()) {
				currentlySelected.push(result);
				continue;
			}

			if (result.inventoryAvailable) {
				inStockLocations.push(result);
				continue;
			}

			outOfStockLocations.push(result);
		}

		return [
			...currentlySelected,
			...myStore,
			...inStockLocations,
			...outOfStockLocations,
			...locations
		];
	}

	/**
	 * Sets the loading to true/false. Shows and hides the loadingUI
	 * @return {Boolean}
	 */
	setLoading (isLoading) {
		this.isLoading = isLoading;

		if (this.isLoading) {
			this.loadingUI.show();
			return;
		}

		this.loadingUI.hide();
	}

	/**
	 * Sets the LoadingUI if it has not already been set up.
	 */
	setLoadingUI () {
		if (this.loadingUI !== false) {
			return;
		}

		this.loadingUI = new LoadingUI(this.renderRoot.querySelector('.js-bopis-drawer-loading-container'), {
			overlayElement: this.renderRoot.querySelector('.g-bopis-drawer') ?? false
		});
	}

	/**
	 * Adds Event Listeners when applicable
	 */
	setListeners () {
		if (this.modal) {
			this.modal.addEventListener('open', () => {
				if (this.opened) {
					return;
				}

				this.opened = true;
				this.search();
			});
		}

		if (this.type !== 'product') {
			return;
		}

		EventBus.addEventListener('MVPS:productVariantChanged', this.productVariantChanged.bind(this), false);
	}

	/**
	 * Updates applicable product details for API calls when a variant has changed.
	 * @param {Event}	e
	 */
	productVariantChanged (e) {
		const payload = e.detail;

		let inventory = payload.productData?.variant?.customfield_values?.bopis_inventory;
		if (!inventory || inventory === '') {
			inventory = {};
		} else {
			inventory = JSON.parse(inventory);
		}

		// Keeping fields from the parent product
		const fields = {...this.productfields};

		// eslint-disable-next-line camelcase
		fields.bopis_inventory = inventory;

		this.productfields = fields;

		this.reset();
		this.productSearch();
	}

	/**
	 * Gets the Location for the `basketBopisStore`
	 */
	async getBasketStoreInformation () {
		const location = await this.controller.api.singleLocationLookup(this.basketBopisStore.identifier);

		if (location === false) {
			this.basketBopisStore = {};
			return;
		}

		this.basketBopisStore = location;
	}

	/**
	 * If `type` = `basket`, It will update the Basket Option.
	 */
	shopStoreBasketChanged (e) {
		const detail = e.detail;

		if (!detail) {
			return;
		}

		if (detail.basketgroup !== this.basketgroup) {
			return;
		}

		if (!detail.identifier) {
			return;
		}

		this.basketBopisStore = detail.location;

		this.requestUpdate();
	}

	isMyStore (store) {
		return store.identifier?.toString() === this.controller?.store?.state?.myStore?.identifier?.toString();
	}
};

customElements.define('x-bopis-drawer', BopisDrawer);
